import * as React from 'react';

import { ComboBox, type ComboBoxProps } from '@rematter/ui-web';
import { UseQueryResult } from '@tanstack/react-query';

import { ObjectRefRecord } from './types';

import useDebouncedValue from '~/shared/hooks/useDebounce';

export type ObjectRefSelectProps<Sorting> = Omit<
  ComboBoxProps,
  'options' | 'value' | 'onChange' | 'isLoading'
> & {
  value?: string;
  onChange?: (value: string | number | null) => void;
  queryHook: (params: {
    searchTerm?: string;
    sorting?: Sorting[];
    limit?: number;
    offset?: number;
  }) => UseQueryResult<ObjectRefRecord[], Error>;
  sorting?: Sorting;
};

export const ObjectRefSelect = <Sorting,>({
  value,
  onChange,
  queryHook: useDataQuery,
  sorting,
  placeholder,
  ...props
}: ObjectRefSelectProps<Sorting>) => {
  const lockAutoSetRef = React.useRef(false);
  const lockSearchTermRef = React.useRef(false);

  const [searchTerm, setSearchTerm] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

  const { data, isLoading } = useDataQuery({
    searchTerm: debouncedSearchTerm || undefined,
    sorting: sorting ? [sorting] : undefined,
    offset: 0,
    limit: 50,
  });

  const options = React.useMemo(
    () =>
      (data ?? []).map((item) => ({
        id: item.id,
        label: item.name,
      })),
    [data],
  );

  const handleSearch = React.useCallback((term: string) => {
    if (!lockSearchTermRef.current) {
      setSearchTerm(term);
    }
  }, []);

  React.useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      lockSearchTermRef.current = true;
    }
  }, [isOpen]);

  // If value is not set and there is only one option, set it to the first option
  React.useEffect(() => {
    if (!value && options.length === 1 && !lockAutoSetRef.current) {
      onChange?.(options[0].id);
      lockAutoSetRef.current = true;
    }

    if (options.length > 1) {
      lockAutoSetRef.current = true;
    }
  }, [options, value, onChange]);

  const {
    renderSelection: _renderSelection,
    getSelection: _getSelection,
    ...restProps
  } = props;

  return (
    <ComboBox
      {...restProps}
      options={options}
      value={value ? [value] : []}
      onChange={(values) => onChange?.(values?.[0] ?? null)}
      isLoading={isLoading}
      query={searchTerm}
      onChangeQuery={handleSearch}
      placeholder={placeholder}
      onDismiss={() => setIsOpen(false)}
      isRequired
      className="@ObjectRefSelect"
      getSelection={(ids) => {
        const item = data?.find((d) => d.id === ids[0]);
        return item ? [{ label: item.name }] : [];
      }}
    />
  );
};
